import {
  hashKey,
  MutationCache,
  type Query,
  QueryCache,
  QueryClient,
  type QueryFilters,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import { escopoDoCache, PREFIXO_HASH_DELEGADO } from './delegacao/atuacao';
import { classifyError, getUserVisibleErrorMessage } from './errors';
import { reportClassifiedError } from './observability/sentry';

// Deduplicação: evitar múltiplos toasts iguais em rajada
let lastToastMessage = '';
let lastToastTime = 0;
const TOAST_DEDUP_MS = 2000;

function deduplicatedToast(message: string) {
  const now = Date.now();
  if (message === lastToastMessage && now - lastToastTime < TOAST_DEDUP_MS) return;
  lastToastMessage = message;
  lastToastTime = now;
  toast.error(message);
}

/**
 * Cria o QueryClient com a política global de erros do JurisApp.
 *
 * Regras:
 * - QueryCache: queries silenciosas por padrão; 401 ignorado; 429 sempre toast;
 *   opt-in via meta.showToastOnError.
 * - MutationCache: toast automático se mutation não tem onError próprio;
 *   401 ignorado (redirect já em andamento).
 * - defaultOptions: staleTime 5min, gcTime 7d, offlineFirst, retry inteligente.
 */
/**
 * Invalidação e refetch só no contexto ATIVO (docs/PLANO_DELEGACAO.md). A filtragem
 * do React Query é por queryKey, não por hash: sem isto, uma invalidação com
 * `refetchType: 'all'` (ou um `refetchQueries` por chave parcial) refaria a query
 * INATIVA do outro contexto com o header do contexto atual — e dado do titular
 * iria parar na entrada da própria conta, que é persistida em disco.
 */
function doEscopoAtivo(query: Query): boolean {
  const escopo = escopoDoCache();
  return escopo
    ? query.queryHash.startsWith(`${PREFIXO_HASH_DELEGADO}${escopo}:`)
    : !query.queryHash.startsWith(PREFIXO_HASH_DELEGADO);
}

function comEscopo<T extends QueryFilters>(filtros?: T): T {
  const original = filtros?.predicate;
  return {
    ...(filtros ?? ({} as T)),
    predicate: (q: Query) => doEscopoAtivo(q) && (original ? original(q) : true),
  };
}

class QueryClientPorContexto extends QueryClient {
  override invalidateQueries(...[filtros, opcoes]: Parameters<QueryClient['invalidateQueries']>) {
    return super.invalidateQueries(comEscopo(filtros), opcoes);
  }

  override refetchQueries(...[filtros, opcoes]: Parameters<QueryClient['refetchQueries']>) {
    return super.refetchQueries(comEscopo(filtros), opcoes);
  }

  override resetQueries(...[filtros, opcoes]: Parameters<QueryClient['resetQueries']>) {
    return super.resetQueries(comEscopo(filtros), opcoes);
  }
}

export function createQueryClient(): QueryClient {
  return new QueryClientPorContexto({
    queryCache: new QueryCache({
      onError: (error, query) => {
        const classified = classifyError(error);
        reportClassifiedError(error, classified);

        // 401: interceptor já tratou, nunca gerar UX
        if (classified.isAuthError) return;

        // 429: sempre toast (rate-limit — usuário precisa saber)
        if (classified.kind === 'rate_limited') {
          deduplicatedToast(classified.message);
          return;
        }

        // Queries silenciosas por padrão — cada tela trata error inline.
        // Opt-in via meta.showToastOnError para casos excepcionais.
        if (query.meta?.showToastOnError) {
          deduplicatedToast(classified.message);
        }
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        // Se mutation já tem onError próprio, ela (via notifyMutationError)
        // já reportou ao Sentry — não duplicar nem toast nem evento.
        if (mutation.options.onError) return;

        reportClassifiedError(error);
        const message = getUserVisibleErrorMessage(error);
        if (message) deduplicatedToast(message);
      },
    }),
    defaultOptions: {
      queries: {
        // Cache separado por contexto (docs/PLANO_DELEGACAO.md): no contexto delegado o
        // hash ganha o titular. Mesmo queryKey, entradas distintas — dado de uma conta
        // nunca aparece na outra, nem o de uma requisição que estava em voo na troca.
        // Na própria conta o hash é o de sempre (snapshot persistido continua válido).
        queryKeyHashFn: (queryKey) => {
          const escopo = escopoDoCache();
          const hash = hashKey(queryKey);
          return escopo ? `${PREFIXO_HASH_DELEGADO}${escopo}:${hash}` : hash;
        },
        staleTime: 5 * 60 * 1000,
        gcTime: 7 * 24 * 60 * 60 * 1000, // 7 days for IDB persistence
        networkMode: 'offlineFirst',
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          const status = (error as { response?: { status?: number } })?.response?.status;
          if (status && status >= 400 && status < 500) return false;
          return failureCount < 2;
        },
      },
    },
  });
}
